import { useState, useEffect } from 'react';
import { User, Mail, Shield, Search, Plus, FolderOpen, Pencil, Trash2 } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { toast } from 'sonner';
import FolderPermissions from './FolderPermissions';
import { authFetch } from '../lib/api';

const UsersView = ({ user }) => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showPermissionsDialog, setShowPermissionsDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editRole, setEditRole] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    role: 'Client'
  });

  useEffect(() => {
    if (user.role !== 'Admin') {
      toast.error('Access denied. Admin only.');
      return;
    }
    fetchUsers();
  }, [user.role]);  

  useEffect(() => {
    if (searchQuery) {
      setFilteredUsers(
        users.filter(u => 
          u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          u.email.toLowerCase().includes(searchQuery.toLowerCase())
        )
      );
    } else {
      setFilteredUsers(users);
    }
  }, [searchQuery, users]);

  const fetchUsers = async () => {
    try {
      
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/users`, {
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
        setFilteredUsers(data.users);
      }
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();

    try {
      
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newUser)
      });

      if (response.ok) {
        toast.success(`${newUser.role} created successfully! They can now login via Google SSO.`);
        setShowCreateDialog(false);
        setNewUser({ name: '', email: '', role: 'Client' });
        fetchUsers();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to create user');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
    }
  };

  const openEditDialog = (u) => {
    setEditingUser(u);
    setEditRole(u.role || 'Client');
    setShowEditDialog(true);
  };

  const handleSaveRole = async () => {
    if (!editingUser) return;
    try {
      const response = await authFetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/${editingUser.id}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ role: editRole }),
        },
      );
      if (response.ok) {
        toast.success(`${editingUser.name} is now ${editRole.replace('_', ' ')}`);
        setShowEditDialog(false);
        setEditingUser(null);
        fetchUsers();
      } else {
        const data = await response.json().catch(() => ({}));
        toast.error(data.detail || 'Failed to update user');
      }
    } catch {
      toast.error('Network error. Please try again.');
    }
  };

  const handleDeleteUser = async (u) => {
    if (u.id === user.id) {
      toast.error('You cannot delete your own account');
      return;
    }
    if (!window.confirm(`Delete ${u.name} (${u.email})? This also removes their email from the approved list.`)) {
      return;
    }
    try {
      const response = await authFetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/${u.id}`,
        { method: 'DELETE' },
      );
      if (response.ok) {
        toast.success(`${u.name} deleted`);
        fetchUsers();
      } else {
        const data = await response.json().catch(() => ({}));
        toast.error(data.detail || 'Failed to delete user');
      }
    } catch {
      toast.error('Network error. Please try again.');
    }
  };
  if (user.role !== 'Admin') {
    return null;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand"></div>
      </div>
    );
  }

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'Admin':
        return 'bg-negative/20 text-negative';
      case 'Media_Buyer':
        return 'bg-brand/20 text-brand';
      case 'Client':
        return 'bg-positive/20 text-positive';
      default:
        return 'bg-neutral/20 text-text-primary';
    }
  };

  return (
    <div className="space-y-6" data-testid="users-view">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-semibold tracking-tight text-text-primary mb-2">Users</h2>
          <p className="text-text-secondary">Manage user accounts and permissions</p>
        </div>
        
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="bg-brand hover:bg-brand-hover text-white" data-testid="create-user-button">
              <Plus size={20} className="mr-2" />
              Create User
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white">
            <DialogHeader>
              <DialogTitle className="text-brand">Create New User</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateUser} className="space-y-4" data-testid="create-user-form">
              <div>
                <Label htmlFor="user-name">Full Name</Label>
                <Input
                  id="user-name"
                  placeholder="John Doe"
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  required
                  className="mt-2"
                  data-testid="user-name-input"
                />
              </div>
              <div>
                <Label htmlFor="user-email">Email</Label>
                <Input
                  id="user-email"
                  type="email"
                  placeholder="john@beebyclarkmeyler.com"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  required
                  className="mt-2"
                  data-testid="user-email-input"
                />
                <p className="text-xs text-text-secondary mt-1">
                  User will log in via Google SSO with this email
                </p>
              </div>
              <div>
                <Label htmlFor="user-role">Role</Label>
                <select
                  id="user-role"
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  className="w-full mt-2 px-3 py-2 border border-input rounded-xl bg-white focus:ring-2 focus:ring-brand"
                  data-testid="user-role-select"
                >
                  <option value="Client">Client</option>
                  <option value="Media_Buyer">Media Buyer</option>
                  <option value="Media_Analyst">Media Analyst</option>
                  <option value="Creative">Creative</option>
                  <option value="Admin">Admin</option>
                </select>
                <p className="text-xs text-text-secondary mt-2">
                  {newUser.role === 'Media_Analyst' && '✓ Can create campaigns (needs approval), access assigned clients, export data'}
                  {newUser.role === 'Media_Buyer' && '✓ Can approve campaigns, manage clients, full access'}
                  {newUser.role === 'Admin' && '✓ Full system access'}
                  {newUser.role === 'Client' && '✓ View-only access to their projects'}
                  {newUser.role === 'Creative' && '✓ Asset management and creative workflows'}
                </p>
              </div>
              <div className="bg-brand/10 border border-brand/20 rounded-lg p-4">
                <p className="text-sm text-text-primary mb-2">
                  <strong>Google Drive Integration:</strong>
                </p>
                <p className="text-xs text-text-secondary">
                  {newUser.role === 'Client' ? (
                    <>
                      • A client folder will be automatically created in Google Drive<br/>
                      • Standard folder structure: 1.0 SOW, 2.0 Estimate, 3.0 Production Assets, 4.0 Background Documents, 5.0 Schedule, 6.0 Presentations, 7.0 Reporting, 8.0 Data<br/>
                      • All project assets will be organized in their dedicated folders
                    </>
                  ) : (
                    'No Drive folder will be created for non-client roles'
                  )}
                </p>
              </div>
              <div className="bg-brand/10 border border-brand/20 rounded-lg p-4">
                <p className="text-sm text-text-primary">
                  <strong>Next steps:</strong> Share the login credentials with the user via email. 
                  They can login at: <span className="font-mono text-brand">{window.location.origin}/login</span>
                </p>
              </div>
              <Button type="submit" className="w-full bg-brand hover:bg-brand-hover text-white" data-testid="submit-user">
                Create User Account
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-text-tertiary" size={20} />
        <Input
          placeholder="Search users..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-12 glass"
          data-testid="users-search-input"
        />
      </div>

      {/* Users Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="users-grid">
        {filteredUsers.map((userItem) => (
          <Card key={userItem.id} className="glass" data-testid={`user-card-${userItem.id}`}>
            <CardContent className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-brand to-brand-hover flex items-center justify-center text-white font-bold text-lg">
                  {userItem.name.charAt(0)}
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-text-primary">{userItem.name}</h3>
                  <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold mt-1 ${getRoleBadgeColor(userItem.role)}`}>
                    {userItem.role.replace('_', ' ')}
                  </span>
                </div>
              </div>
              <div className="space-y-2 text-sm text-text-secondary mb-4">
                <div className="flex items-center gap-2">
                  <Mail size={16} />
                  <span className="truncate">{userItem.email}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Shield size={16} />
                  <span>ID: {userItem.id.substring(0, 8)}...</span>
                </div>
              </div>
              <Button
                onClick={() => {
                  setSelectedUser(userItem);
                  setShowPermissionsDialog(true);
                }}
                variant="outline"
                size="sm"
                className="w-full border-brand/20 text-brand hover:bg-brand-hover/10"
                data-testid={`manage-folders-${userItem.id}`}
              >
                <FolderOpen size={16} className="mr-2" />
                Manage Folders
              </Button>
              <div className="flex gap-2 mt-2">
                <Button
                  onClick={() => openEditDialog(userItem)}
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  data-testid={`edit-user-${userItem.id}`}
                >
                  <Pencil size={14} className="mr-1.5" />
                  Edit Role
                </Button>
                <Button
                  onClick={() => handleDeleteUser(userItem)}
                  variant="outline"
                  size="sm"
                  disabled={userItem.id === user.id}
                  className="flex-1 border-negative/30 text-negative hover:bg-negative/10 disabled:opacity-40"
                  data-testid={`delete-user-${userItem.id}`}
                >
                  <Trash2 size={14} className="mr-1.5" />
                  Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Folder Permissions Dialog */}
      {showPermissionsDialog && selectedUser && (
        <Dialog open={showPermissionsDialog} onOpenChange={setShowPermissionsDialog}>
          <DialogContent className="bg-white max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-brand">Folder Permissions</DialogTitle>
            </DialogHeader>
            <FolderPermissions
              userId={selectedUser.id}
              userName={selectedUser.name}
              userRole={selectedUser.role}
              onClose={() => {
                setShowPermissionsDialog(false);
                setSelectedUser(null);
                fetchUsers();
              }}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Role Dialog */}
      {showEditDialog && editingUser && (
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="bg-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-brand">Edit Role — {editingUser.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="text-sm text-text-secondary">
                <p><strong>Email:</strong> {editingUser.email}</p>
                <p><strong>Current role:</strong> {editingUser.role.replace('_', ' ')}</p>
              </div>
              <div>
                <Label htmlFor="edit-user-role">New role</Label>
                <select
                  id="edit-user-role"
                  value={editRole}
                  onChange={(e) => setEditRole(e.target.value)}
                  className="w-full mt-2 px-3 py-2 border border-input rounded-xl bg-white focus:ring-2 focus:ring-brand"
                  data-testid="edit-user-role-select"
                >
                  <option value="Client">Client</option>
                  <option value="Client_Service">Client Service</option>
                  <option value="Media_Buyer">Media Buyer</option>
                  <option value="Media_Analyst">Media Analyst</option>
                  <option value="Creative">Creative</option>
                  <option value="Admin">Admin</option>
                </select>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => { setShowEditDialog(false); setEditingUser(null); }}
                  data-testid="edit-user-cancel"
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-brand hover:bg-brand-hover text-white"
                  onClick={handleSaveRole}
                  disabled={editRole === editingUser.role}
                  data-testid="edit-user-save"
                >
                  Save
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {filteredUsers.length === 0 && (
        <div className="text-center py-12">
          <User className="mx-auto text-text-tertiary mb-4" size={48} />
          <h3 className="text-xl font-semibold text-text-primary mb-2">No users found</h3>
          <p className="text-text-secondary">Try adjusting your search</p>
        </div>
      )}
    </div>
  );
};

export default UsersView;
